import { useContext } from 'react';
import PropTypes from 'prop-types';
import { Message } from 'semantic-ui-react';
import { defineMessages, useIntl } from 'react-intl';
import imageBlockSVG from '@plone/volto/components/manage/Blocks/Image/block-image.svg';
import { flattenToAppURL, isInternalURL } from '@plone/volto/helpers/Url/Url';
import MaybeWrap from '@plone/volto/components/manage/MaybeWrap/MaybeWrap';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import cx from 'classnames';
import config from '@plone/volto/registry';
import { GridContext } from '@plone/volto/components/manage/Blocks/Grid/context';
import ContentImage from 'volto-uni-bonn/components/Image/ContentImage';
import Copyright from 'volto-uni-bonn/components/Image/Copyright';
import useImageCopyright from 'volto-uni-bonn/components/Image/useImageCopyright';

const messages = defineMessages({
  ReadMore: {
    id: 'Read more',
    defaultMessage: 'Read more',
  },
  PleaseChooseContent: {
    id: 'Please choose an existing content as source for this element',
    defaultMessage:
      'Please choose an existing content as source for this element',
  },
});

const TeaserDefaultTemplate = (props) => {
  const { className, data, isContainer: inGrid, isEditMode, style } = props;
  const intl = useIntl();
  const href = data.href?.[0];
  const image = data.preview_image?.[0];
  const url = data.preview_image?.[0]?.['@id'];
  const copyright = useImageCopyright(
    isEditMode ? url || href?.['@id'] : undefined,
    data.copyright,
  );

  const Image = config.getComponent('Image').component;
  const { openExternalLinkInNewTab } = config.settings;
  const columns = useContext(GridContext);
  const sizes = config.blocks.blocksConfig.teaser.getSizes({
    data,
    inGrid,
    columns,
  });

  return (
    <div className={cx('block teaser bonn-teaser', className)} style={style}>
      <>
        {!href && !data.title && isEditMode && (
          <Message>
            <div className="teaser-item placeholder">
              <Image src={imageBlockSVG} alt="" />
              <p>{intl.formatMessage(messages.PleaseChooseContent)}</p>
            </div>
          </Message>
        )}
        {(href || data.title) && (
          <article
            className={cx('teaser-item default bonn-card', {
              'bonn-card-cover': data.variation === 'cover',
            })}
          >
            <figure className="image-wrapper">
              <ContentImage
                fallback="card"
                fallbackKey={data.title || href?.title || ''}
                src={
                  url && !image?.image_field
                    ? isInternalURL(url) && !url.includes('/@@images/')
                      ? `${flattenToAppURL(url)}/@@images/image`
                      : url
                    : undefined
                }
                item={url && !image?.image_field ? undefined : image || href}
                imageField={image?.image_field || href?.image_field}
                alt=""
                loading="lazy"
                responsive
                sizes={sizes}
              >
                <Copyright>{copyright}</Copyright>
              </ContentImage>
            </figure>
            <div className="content">
              {data?.head_title && (
                <div className="headline">{data.head_title}</div>
              )}
              <h3 className="bonn-card-title">{data?.title}</h3>
              {!data.hide_description && data.description && (
                <p>{data.description}</p>
              )}
              {href && (
                <div className="bonn-card-action">
                  <MaybeWrap
                    condition={!isEditMode}
                    as={UniversalLink}
                    href={href['@id']}
                    aria-label={`${data.buttonLabel || intl.formatMessage(messages.ReadMore)}: ${data.title || href.Title || href.title || href['@id']}`}
                    className="bonn-card-button"
                    target={
                      data.openLinkInNewTab ||
                      (openExternalLinkInNewTab && !isInternalURL(href['@id']))
                        ? '_blank'
                        : null
                    }
                  >
                    <span
                      className={isEditMode ? 'bonn-card-button' : undefined}
                    >
                      {data.buttonLabel ||
                        intl.formatMessage(messages.ReadMore)}
                    </span>
                  </MaybeWrap>
                </div>
              )}
            </div>
          </article>
        )}
      </>
    </div>
  );
};

TeaserDefaultTemplate.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  isEditMode: PropTypes.bool,
};

export default TeaserDefaultTemplate;
